
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { AceInfo } from '@/lib/aces-data';
import { editorInputToRecord, type AceCmsRecord, type AceEditorInput, type AcesCmsState } from '@/lib/aces-cms';
import { isPubliclyVisible, type CmsPublicationStatus } from '@/lib/cms-publication';
import * as acesService from '@/services/aces.service';
import { hasApiAuthToken, ApiError } from '@/services/api-client';
import { toast } from 'sonner';

type AcesCmsContextValue = {
  aces: AceCmsRecord[];
  publicAces: AceCmsRecord[];
  hydrated: boolean;
  addAce: (input: AceEditorInput) => Promise<AceCmsRecord>;
  updateAce: (slug: string, input: AceEditorInput) => Promise<void>;
  setAcePublicationStatus: (slug: string, status: CmsPublicationStatus) => void;
  deleteAce: (slug: string) => void;
  getAceBySlug: (slug: string) => AceCmsRecord | undefined;
  resetToMockSeed: () => void;
};

const AcesCmsContext = createContext<AcesCmsContextValue | null>(null);

function syncPublishedAce(list: AceCmsRecord[], ace: AceCmsRecord): AceCmsRecord[] {
  const without = list.filter((item) => item.slug !== ace.slug);
  if (!isPubliclyVisible(ace.publicationStatus)) return without;
  return [...without, ace];
}

function mergePublicAcesPreferNewer(remote: AceCmsRecord[], local: AceCmsRecord[]): AceCmsRecord[] {
  const bySlug = new Map(remote.map((a) => [a.slug.toLowerCase(), a]));
  for (const ace of local) {
    const key = ace.slug.toLowerCase();
    const current = bySlug.get(key);
    if (!current) {
      if (isPubliclyVisible(ace.publicationStatus)) bySlug.set(key, ace);
      continue;
    }
    const localTs = Date.parse(ace.updatedAt) || 0;
    const remoteTs = Date.parse(current.updatedAt) || 0;
    if (localTs >= remoteTs) bySlug.set(key, ace);
  }
  return Array.from(bySlug.values());
}

export function AcesCmsProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const isPainelRoute = location.pathname.startsWith('/painel');
  const [state, setState] = useState<AcesCmsState>({ aces: [] });
  const [publicAces, setPublicAces] = useState<AceCmsRecord[]>([]);
  const [publicHydrated, setPublicHydrated] = useState(false);
  const [adminHydrated, setAdminHydrated] = useState(false);
  const adminReadyRef = useRef(false);

  const hydrated = isPainelRoute ? adminHydrated : publicHydrated;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const pub = await acesService.getPublicAces();
        if (!cancelled) {
          setPublicAces((prev) => mergePublicAcesPreferNewer(pub, prev));
        }
      } catch {
        if (!cancelled) setPublicAces((prev) => prev);
      } finally {
        if (!cancelled) setPublicHydrated(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAdmin = async () => {
      if (!isPainelRoute || !hasApiAuthToken()) {
        if (!cancelled) {
          adminReadyRef.current = true;
          setAdminHydrated(true);
        }
        return;
      }

      // Não desidratar em refetch (ex.: refresh JWT ao voltar à aba).
      if (!adminReadyRef.current && !cancelled) setAdminHydrated(false);

      try {
        const admin = await acesService.getAdminAces();
        if (!cancelled) {
          setState((s) => ({
            aces: mergePublicAcesPreferNewer(admin, s.aces),
          }));
        }
      } catch {
        /* mantém estado local */
      } finally {
        if (!cancelled) {
          adminReadyRef.current = true;
          setAdminHydrated(true);
        }
      }
    };

    void loadAdmin();

    const onAuthChange = () => {
      void loadAdmin();
    };

    window.addEventListener('xat-auth-changed', onAuthChange);
    return () => {
      cancelled = true;
      window.removeEventListener('xat-auth-changed', onAuthChange);
    };
  }, [isPainelRoute]);

  const getAceBySlug = useCallback(
    (slug: string) => {
      const key = slug.toLowerCase();
      return (
        state.aces.find((a) => a.slug === key) ??
        publicAces.find((a) => a.slug === key)
      );
    },
    [state.aces, publicAces]
  );

  const addAce = useCallback(async (input: AceEditorInput) => {
    const row = editorInputToRecord(input);
    setState((s) => ({
      aces: [...s.aces.filter((a) => a.slug !== row.slug), row],
    }));
    setPublicAces((prev) => syncPublishedAce(prev, row));

    try {
      const saved = await acesService.createAce(row);
      setState((s) => ({
        aces: [...s.aces.filter((a) => a.slug !== row.slug && a.slug !== saved.slug), saved],
      }));
      setPublicAces((prev) => syncPublishedAce(prev.filter((a) => a.slug !== row.slug), saved));
      return saved;
    } catch (err) {
      setState((s) => ({ aces: s.aces.filter((a) => a.slug !== row.slug) }));
      setPublicAces((prev) => prev.filter((a) => a.slug !== row.slug));
      toast.error('Não foi possível criar o ace', {
        description: err instanceof ApiError ? err.message : 'Tente novamente.',
      });
      throw err;
    }
  }, []);

  const updateAce = useCallback(async (slug: string, input: AceEditorInput) => {
    const key = slug.toLowerCase();
    const nextSlug = input.slug.trim().toLowerCase();
    let snapshot: AceCmsRecord | undefined;
    let optimisticRow: AceCmsRecord | null = null;
    setState((s) => {
      const existing = s.aces.find((a) => a.slug === key);
      if (!existing) return s;
      snapshot = existing;
      const updated = editorInputToRecord(input, existing);
      optimisticRow = updated;
      const without = s.aces.filter((a) => a.slug !== key && a.slug !== nextSlug);
      return { aces: [...without, updated] };
    });
    if (optimisticRow) {
      setPublicAces((prev) => syncPublishedAce(prev, optimisticRow!));
    }

    try {
      const saved = await acesService.updateAce(key, editorInputToRecord(input));
      setState((s) => ({
        aces: [...s.aces.filter((a) => a.slug !== key && a.slug !== saved.slug), saved],
      }));
      setPublicAces((prev) => syncPublishedAce(prev, saved));
    } catch (err) {
      if (snapshot) {
        setState((s) => ({
          aces: [...s.aces.filter((a) => a.slug !== key && a.slug !== snapshot!.slug), snapshot!],
        }));
        setPublicAces((prev) => syncPublishedAce(prev, snapshot!));
      }
      toast.error('Não foi possível guardar o ace', {
        description: err instanceof ApiError ? err.message : 'Tente novamente.',
      });
      throw err;
    }
  }, []);

  const setAcePublicationStatus = useCallback((slug: string, status: CmsPublicationStatus) => {
    const key = slug.toLowerCase();
    setState((s) => ({
      aces: s.aces.map((a) =>
        a.slug === key ? { ...a, publicationStatus: status, updatedAt: new Date().toISOString() } : a
      ),
    }));

    void acesService
      .updateAce(key, { publicationStatus: status })
      .then((saved) => {
        setState((s) => ({
          aces: s.aces.map((a) => (a.slug === key ? saved : a)),
        }));
        setPublicAces((prev) => syncPublishedAce(prev, saved));
      })
      .catch(() => undefined);
  }, []);

  const deleteAce = useCallback((slug: string) => {
    const key = slug.toLowerCase();
    setState((s) => ({ aces: s.aces.filter((a) => a.slug !== key) }));
    setPublicAces((prev) => prev.filter((a) => a.slug !== key));

    void acesService.deleteAce(key).catch(() => undefined);
  }, []);

  const resetToMockSeed = useCallback(() => {
    void acesService.getAdminAces().then((admin) => {
      setState({ aces: admin });
    });
  }, []);

  const value = useMemo<AcesCmsContextValue>(
    () => ({
      aces: state.aces,
      publicAces,
      hydrated,
      addAce,
      updateAce,
      setAcePublicationStatus,
      deleteAce,
      getAceBySlug,
      resetToMockSeed,
    }),
    [state.aces, publicAces, hydrated, addAce, updateAce, setAcePublicationStatus, deleteAce, getAceBySlug, resetToMockSeed]
  );

  return <AcesCmsContext.Provider value={value}>{children}</AcesCmsContext.Provider>;
}

export function useAcesCms() {
  const ctx = useContext(AcesCmsContext);
  if (!ctx) throw new Error('useAcesCms must be used within AcesCmsProvider');
  return ctx;
}

export function useCatalogAces(): { aces: AceInfo[]; hydrated: boolean } {
  const ctx = useContext(AcesCmsContext);
  const aces = useMemo(
    () =>
      (ctx?.publicAces ?? []).filter((a) => isPubliclyVisible(a.publicationStatus)) as AceInfo[],
    [ctx?.publicAces]
  );
  return { aces, hydrated: ctx?.hydrated ?? false };
}
